import { SortTable } from '@nilohealth/core-ui'
import { useRouter } from 'next/router'
import { UseQueryResult, useQuery } from 'react-query'
import { FilterForm } from './filters/filters.types'
import { generateMetricFilters } from './metrics.logic'
import { getMassGuidelineMetricsById, getMetricById } from './metrics.services'
import { Metric } from './metrics.types'

export type UseMetricProps = {
  metricId: string
  selectedFilters?: FilterForm
  pagination?: { offset: number; limit: number }
  sort?: SortTable<unknown> | undefined
}

export type UseMetricResult<T extends Metric> = Omit<UseQueryResult<T>, 'data'> & { metric?: T }

export type UseMetricHook = <T extends Metric>(input: UseMetricProps) => UseMetricResult<T>

export const useMetric = <T extends Metric>({
  metricId,
  selectedFilters,
  pagination,
  sort
}: UseMetricProps): UseMetricResult<T> => {
  const filters = selectedFilters ? generateMetricFilters(selectedFilters) : {}
  const pag = pagination ?? {}
  const orderBy = sort
    ? { order_by: [{ field_name: sort.keyValueToSort, direction: sort.direction.toUpperCase() }] }
    : {}

  const body = { ...filters, ...pag, ...orderBy }

  const queryKey = ['metric', metricId, pagination, selectedFilters, sort]
  const queryOptions = { refetchOnWindowFocus: false, staleTime: 4 * 60 * 60 * 1000 }
  const queryFunc = () => getMetricById<T>(metricId, body)

  const { data: metric, ...rest } = useQuery(queryKey, queryFunc, queryOptions)

  return { metric, ...rest }
}

export const useMassGuidelineMetrics = <T extends Metric>({
  metricId,
  selectedFilters,
  pagination,
}: UseMetricProps): UseMetricResult<T> => {
  const filters = selectedFilters ? generateMetricFilters(selectedFilters) : {}
  const pag = pagination ?? {}

  const body = { ...filters, ...pag }

  const queryKey = ['mass-guideline-application', metricId, pagination, selectedFilters]
  const queryOptions = { refetchOnWindowFocus: false, staleTime: 4 * 60 * 60 * 1000 }
  const queryFunc = () => getMassGuidelineMetricsById<T>(metricId, body)

  const { data: metric, ...rest } = useQuery(queryKey, queryFunc, queryOptions)

  return { metric, ...rest }
}

export const useMassGuidelineMetricsDetails = <T extends Metric>({
  metricId,
  selectedFilters,
  pagination,
}: UseMetricProps): UseMetricResult<T> => {
  const filters = selectedFilters ? generateMetricFilters(selectedFilters) : {}
  const pag = pagination ?? {}
  const router = useRouter()

  const applicationId = router.query.applicationId as string

  const body = { ...filters, ...pag, application_id: parseInt(applicationId, 10) }

  const queryKey = ['mass-guideline-application-details', metricId, pagination, selectedFilters, applicationId]
  const queryOptions = { refetchOnWindowFocus: false, staleTime: 4 * 60 * 60 * 1000 }
  const queryFunc = () => getMassGuidelineMetricsById<T>(metricId, body)

  const { data: metric, ...rest } = useQuery(queryKey, queryFunc, queryOptions)

  return { metric, ...rest }
}
